import React, { createContext, useState, useEffect, useContext } from 'react';
import notesService from '../services/notesService';
import categoriesService from '../services/categoriesService';
import { useAuth } from './AuthContext';

const NotesContext = createContext(null);

export const NotesProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [notes, setNotes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedNote, setSelectedNote] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch notes and categories when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchNotes();
      fetchCategories();
    } else {
      // Clear data when not authenticated
      setNotes([]);
      setCategories([]);
      setSelectedNote(null);
      setSelectedCategory(null);
    }
  }, [isAuthenticated]);

  const fetchNotes = async (filters = {}) => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await notesService.getNotes(filters);
      setNotes(data.notes || []);
    } catch (err) {
      console.error('Failed to fetch notes:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      setError(null);
      const data = await categoriesService.getCategories();
      setCategories(data.categories || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
      setError(err.message);
    }
  };

  const createNote = async (noteData) => {
    try {
      setError(null);
      const data = await notesService.createNote(noteData);

      // Optimistic update
      setNotes([data.note, ...notes]);
      setSelectedNote(data.note);

      // Refresh categories to update note counts
      fetchCategories();

      return { success: true, note: data.note };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const updateNote = async (noteId, noteData) => {
    try {
      setError(null);
      const data = await notesService.updateNote(noteId, noteData);

      // Optimistic update
      setNotes(notes.map(note => note.id === noteId ? data.note : note));
      if (selectedNote && selectedNote.id === noteId) {
        setSelectedNote(data.note);
      }

      // Refresh categories if category was changed to update note counts
      if ('category_id' in noteData) {
        fetchCategories();
      }

      return { success: true, note: data.note };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const deleteNote = async (noteId) => {
    try {
      setError(null);
      await notesService.deleteNote(noteId);

      // Optimistic update
      setNotes(notes.filter(note => note.id !== noteId));
      if (selectedNote && selectedNote.id === noteId) {
        setSelectedNote(null);
      }

      // Refresh categories to update note counts
      fetchCategories();

      return { success: true };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const createCategory = async (categoryData) => {
    try {
      setError(null);
      const data = await categoriesService.createCategory(categoryData);

      // Optimistic update
      setCategories([data.category, ...categories]);

      return { success: true, category: data.category };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const updateCategory = async (categoryId, categoryData) => {
    try {
      setError(null);
      const data = await categoriesService.updateCategory(categoryId, categoryData);

      // Optimistic update
      setCategories(categories.map(cat => cat.id === categoryId ? data.category : cat));

      return { success: true, category: data.category };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const deleteCategory = async (categoryId) => {
    try {
      setError(null);
      await categoriesService.deleteCategory(categoryId);

      // Optimistic update
      setCategories(categories.filter(cat => cat.id !== categoryId));
      if (selectedCategory && selectedCategory.id === categoryId) {
        setSelectedCategory(null);
        fetchNotes(); // Refresh notes as their categories may have changed
      }

      return { success: true };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const selectNote = (note) => {
    setSelectedNote(note);
  };

  const selectCategory = (category) => {
    setSelectedCategory(category);
    if (category) {
      fetchNotes({ category_id: category.id });
    } else {
      fetchNotes();
    }
  };

  const value = {
    notes,
    categories,
    selectedNote,
    selectedCategory,
    isLoading,
    error,
    fetchNotes,
    fetchCategories,
    createNote,
    updateNote,
    deleteNote,
    createCategory,
    updateCategory,
    deleteCategory,
    selectNote,
    selectCategory,
  };

  return <NotesContext.Provider value={value}>{children}</NotesContext.Provider>;
};

// Custom hook to use the notes context
export const useNotes = () => {
  const context = useContext(NotesContext);
  if (!context) {
    throw new Error('useNotes must be used within a NotesProvider');
  }
  return context;
};

export default NotesContext;
